// chatContext/useAppContextEnvelope.ts
//
// The one hook every chat surface calls. It reads the named route id off the
// router handles, the study activity off the study slices, and the anchor /
// selection / prefill off the app-context store, and hands back the envelope
// plus the "may chat be shown at all" flag.

import { useEffect, useId, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatches } from 'react-router-dom';

import { selectActiveBlock } from '../slices/blocks/blocksSlice';
import {
  selectActiveQuestionIndex,
  selectStudyPhase,
} from '../slices/study/studySlice';
import type { RootState } from '../store/store';
import {
  CHAT_ROUTE_IDS,
  type ChatAppContextEnvelope,
  type ChatFlashcardScope,
  type ChatRouteId,
  type ChatSurface,
} from '../types/chatEnvelope';
import {
  clearChatSelectionAndPrefill,
  clearPendingChatSend,
  setDetachedChatContextKey,
  useChatAppContextInputs,
} from './chatAppContextStore';
import { chatContextKey } from './chatContextKey';
import {
  publishAppContextEnvelope,
  retractAppContextEnvelope,
} from './currentAppContextEnvelope';
import {
  deriveAppContextEnvelope,
  deriveChatBlockContext,
  type AppContextActiveBlock,
  type AppContextEnvelopeResult,
} from './deriveAppContextEnvelope';
import type { ChatBlockContext } from './chatBlockThread';

/** What a route puts in its `handle` to name itself to the companion. */
export interface ChatRouteHandle {
  chatRoute?: ChatRouteId;
}

interface RouteMatchLike {
  handle?: unknown;
}

/**
 * The deepest matched route that names itself wins, mirroring how
 * `StudyPhaseRouteSync` reads `handle.studyPhase`. A route with no handle (and
 * anything the union does not know) reports `other` rather than a path.
 */
export const resolveChatRouteId = (
  matches: readonly RouteMatchLike[]
): ChatRouteId => {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const candidate = (matches[index]?.handle as ChatRouteHandle | undefined)
      ?.chatRoute;

    if (candidate && CHAT_ROUTE_IDS.includes(candidate)) return candidate;
  }

  return 'other';
};

export interface UseAppContextEnvelopeOptions {
  surface: ChatSurface;
  /**
   * Whether this surface publishes its envelope as the one `sendChatTurn` falls
   * back to. Publishers form a stack: the most recently published visible
   * surface wins, and setting this to `false` (the shell went invisible) or
   * unmounting hands the fallback back to the surface underneath. Pass
   * `false` from a mounted-but-hidden shell — a closed drawer — so it cannot
   * speak for the surface the student is actually using.
   */
  publish?: boolean;
}

/** The blocks slice's active block, flattened to what the envelope reads. */
const toAppContextActiveBlock = (
  block: ReturnType<typeof selectActiveBlock>
): AppContextActiveBlock | null =>
  block
    ? {
        id: block.id,
        vignetteCount: block.vignetteCount,
        isSubmitted: block.isSubmitted,
        selfAssessmentAttemptId: block.selfAssessmentAttemptId ?? null,
        selfAssessmentIsReviewable:
          block.selfAssessmentAccess?.isReviewable === true,
      }
    : null;

export const useAppContextEnvelope = ({
  surface,
  publish = true,
}: UseAppContextEnvelopeOptions): AppContextEnvelopeResult => {
  const matches = useMatches();
  const route = useMemo(() => resolveChatRouteId(matches), [matches]);
  const { pathname } = useLocation();

  const {
    anchor,
    selection,
    prefill,
    pendingSend,
    detachedAnchorKey,
    viewing,
    course,
    courseActivity,
  } = useChatAppContextInputs();

  const phase = useSelector(selectStudyPhase);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);

  const activeQuestionBlockId = useSelector(
    (state: RootState) => state.studyPhase.activeQuestionBlockId
  );

  const block = useSelector(selectActiveBlock);

  // Primitives, so a flashcard run advancing a card does not re-render every
  // chat surface through an object identity change.
  const flashcardScope = useSelector(
    (state: RootState) =>
      (state.flashcardRun.scope?.kind ?? null) as ChatFlashcardScope | null
  );

  const flashcardCompletedCount = useSelector(
    (state: RootState) => state.flashcardRun.completedCount
  );

  const flashcardTotalCount = useSelector(
    (state: RootState) => state.flashcardRun.totalCount
  );

  const todaySessionId = useSelector(
    (state: RootState) => state.todaySession.session?.id || null
  );

  const activeBlock = useMemo(() => toAppContextActiveBlock(block), [block]);

  const derived = deriveAppContextEnvelope({
    surface,
    route,
    path: pathname,
    anchor,
    detachedContextKey: detachedAnchorKey,
    selection,
    prefill,
    viewing,
    course,
    courseActivity,
    study: {
      phase,
      activeQuestionIndex,
      activeQuestionBlockId,
      activeBlock,
      flashcardRun: {
        scope: flashcardScope,
        completedCount: flashcardCompletedCount,
        totalCount: flashcardTotalCount,
      },
      todaySessionId,
    },
  });

  // Identity stability: consumers put the envelope in effect and callback
  // dependency lists, so an unchanged context must hand back the same object
  // however often the tree re-renders. Comparing the serialized value is enough
  // — the envelope is small, flat and JSON-only by construction.
  const key = JSON.stringify(derived);
  // `key` stands in for `derived`: same value, comparable by identity.
  const result = useMemo<AppContextEnvelopeResult>(() => derived, [key]);
  const envelope: ChatAppContextEnvelope = result.envelope;
  const isChatAvailable = result.isChatAvailable;
  const publisherId = useId();

  useEffect(() => {
    if (!publish) {
      retractAppContextEnvelope(publisherId);
      return;
    }

    // The gate rides along: the send path reads it before it may attach
    // anything captured off the screen (ORA-629).
    publishAppContextEnvelope(publisherId, envelope, isChatAvailable);

    return () => retractAppContextEnvelope(publisherId);
  }, [envelope, isChatAvailable, publish, publisherId]);

  // Detach is sticky per item, not forever (ORA-662): the moment the student
  // moves to the next question, flips to another card or changes page, the new
  // context comes back attached. The key covers both halves of that — an
  // anchor's own key, or the route for a page pill — so one comparison here
  // retires a stale detach however it went stale.
  const contextKey = chatContextKey(anchor, route);

  useEffect(() => {
    if (detachedAnchorKey === null) return;
    if (detachedAnchorKey === contextKey) return;

    setDetachedChatContextKey(null);
  }, [contextKey, detachedAnchorKey]);

  // Selection, prefill and a staged send all belong to the surface that
  // produced them. A student who highlights a phrase — or asks about a term and
  // leaves before the answer can be sent — and then navigates elsewhere should
  // not have it quoted into, or fired at, some later unrelated turn. (A flow
  // that deliberately carries a selection across a navigation has to pass it to
  // `sendChatTurn` itself.)
  const oneShotRoute = useRef<ChatRouteId | null>(null);

  useEffect(() => {
    if (selection === null && prefill === null && pendingSend === null) {
      oneShotRoute.current = null;
      return;
    }

    if (oneShotRoute.current === null) {
      oneShotRoute.current = route;
      return;
    }

    if (oneShotRoute.current !== route) {
      oneShotRoute.current = null;
      clearChatSelectionAndPrefill();
      clearPendingChatSend();
    }
  }, [pendingSend, prefill, route, selection]);

  return result;
};

/**
 * The QBank block the drawer's thread may follow (ORA-1285), read in the same
 * render as the anchor so the thread key never names the question first and
 * the block a moment later. Null anywhere else; see `deriveChatBlockContext`.
 */
export const useChatBlockContext = (): ChatBlockContext | null => {
  const matches = useMatches();
  const route = useMemo(() => resolveChatRouteId(matches), [matches]);
  const { surfaceAnchor } = useChatAppContextInputs();
  const phase = useSelector(selectStudyPhase);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);

  const activeQuestionBlockId = useSelector(
    (state: RootState) => state.studyPhase.activeQuestionBlockId
  );

  const block = useSelector(selectActiveBlock);
  const activeBlock = useMemo(() => toAppContextActiveBlock(block), [block]);

  const context = deriveChatBlockContext({
    route,
    anchor: surfaceAnchor,
    study: {
      phase,
      activeQuestionIndex,
      activeQuestionBlockId,
      activeBlock,
      flashcardRun: null,
      todaySessionId: null,
    },
  });

  const key = context
    ? `${context.blockId}:${context.index}:${context.total}`
    : null;

  // `key` stands in for `context`: same value, comparable by identity.
  return useMemo(() => context, [key]);
};
